import { useQuery } from "@apollo/react-hooks";
import { Box, useMediaQuery } from "@material-ui/core";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import Imaging from "assets/mrimachinebluebg.svg";
import { BackButton, Button, Loading, StyledGrid } from "components/simple";

import {
  breakpoints,
  color,
  fontFamily,
  fontSize,
  spacingDefaults
} from "style/constants";
import { GetImagingCalendarEvents } from "graphql/sessions/sessions.gql";
import { useParams } from "react-router-dom";
import { format } from "date-fns";

interface ImagingConfirmationParams {
  imagingSessionId: string;
}

const ImagingConfirmation: React.FC = () => {
  const dispatch = useDispatch();
  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);

  const { imagingSessionId } = useParams<ImagingConfirmationParams>();
  const [sessionTime, setSessionTime] = useState<Date>(new Date());
  const [outlookEvent, setOutlookEvent] = useState<string>("");
  const [iCalEvent, setiCalEvent] = useState<string>("");
  const [googleEvent, setGoogleEvent] = useState<string>("");

  const { loading } = useQuery(GetImagingCalendarEvents, {
    variables: {
      id: imagingSessionId
    },
    fetchPolicy: "cache-and-network",
    onCompleted(data) {
      const {
        imagingSession: {
          id,
          sessionTime,
          googleCalendarLink,
          icsFileDetails,
          icalFileDetails
        }
      } = data;
      setSessionTime(new Date(sessionTime));
      setGoogleEvent(googleCalendarLink);
      setOutlookEvent(icsFileDetails);
      setiCalEvent(icalFileDetails);
    },

    onError() {
      dispatch({ type: "server_error", value: true });
    }
  });

  const timezoneAbbreviation = new Date()
    .toLocaleTimeString("en-us", { timeZoneName: "short" })
    .split(" ")[2];

  if (loading) return <Loading />;

  return (
    <StyledGrid
      backgroundColor={color.WHITE}
      container
      direction="column"
      fillRemainingHeight
    >
      <BackButton location="/dashboard" />
      <StyledGrid
        alignItems="center"
        alignSelf="center"
        container
        direction="column"
        item
        marginTop={mobile ? "96px" : "161px"}
        maxWidth="768px"
        padding={mobile ? spacingDefaults.normal : undefined}
        xs={12}
      >
        <img src={Imaging} height={mobile ? "200px" : "300px"} />

        <Box
          component="h1"
          fontFamily={fontFamily.secondary}
          fontSize="30px"
          fontWeight="400"
          letterSpacing="-0.45px"
          lineHeight="41px"
          margin={`${spacingDefaults.large} 0 0 0`}
          textAlign="center"
        >
          Your imaging session is confirmed!
        </Box>
        <Box
          color={color.DARKERGRAY}
          component="p"
          fontSize={fontSize.medium}
          lineHeight="28px"
          margin={`${spacingDefaults.normal} 0 0 0`}
          textAlign="center"
        >
          Your imaging session is scheduled for
          <strong>
            {" "}
            {format(sessionTime, "MMMM d")} at {format(sessionTime, "h:mm aa")}{" "}
            {timezoneAbbreviation}
          </strong>
          . We sent you a confirmation email and additional information will be
          provided closer to the date of your session.
        </Box>
        <StyledGrid
          container
          direction="row"
          item
          justifyContent="space-evenly"
          margin={"1rem 0"}
          marginTop="2rem"
          textAlign="center"
        >
          <StyledGrid
            item
            lg={4}
            margin={`${spacingDefaults.xsmall} 0`}
            xs={12}
          >
            <Button onClick={() => downloadIcsFile(outlookEvent)} width="225px">
              Add to Outlook
            </Button>
          </StyledGrid>
          <StyledGrid
            item
            lg={4}
            margin={`${spacingDefaults.xsmall} 0`}
            xs={12}
          >
            <Button
              background={color.PINK}
              onClick={() => downloadIcsFile(iCalEvent)}
              width="225px"
            >
              Add to iCal
            </Button>
          </StyledGrid>
          <StyledGrid
            item
            lg={4}
            margin={`${spacingDefaults.xsmall} 0`}
            xs={12}
          >
            <Button
              background={color.GREEN}
              longText
              onClick={() => window.open(googleEvent, "_blank")}
              width="225px"
            >
              Add to Google Calendar
            </Button>
          </StyledGrid>
        </StyledGrid>
      </StyledGrid>
    </StyledGrid>
  );

  function downloadIcsFile(input: string): void {
    window.open("data:text/calendar;charset=utf8," + encodeURIComponent(input));
  }
};

export default ImagingConfirmation;
